/**
 * TradeJournal — slide-in drawer anchored to the right edge of the
 * chart area. Three collapsible sections (Open / Pending / Closed)
 * pulled from the live trading SSE slice + the on-demand history fetch.
 *
 * Each row is expandable. The expanded body shows base trade data,
 * an inline free-text "reason" editor (debounced PUT), and a grid
 * of attached PNG screenshots with "Capture chart" + "Upload image"
 * actions. Click a thumbnail to view full-size in a lightbox;
 * hover to reveal the delete button.
 *
 * Persistence is per-user, keyed by `pos:<position_id>` for filled
 * trades and `pending:<ticket>` for pending orders. The same key
 * survives pending → open → closed (we re-key from `pending:` to
 * `pos:` when a pending order fills, via /api/journal/.../rekey).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  useAppDispatch,
  useAppSelector,
  useActiveTabId,
  useCan,
} from "@/state/hooks";
import { CAP } from "@/api/capabilities";
import { fetchTradingHistory } from "@/state/slices/tradingSlice";
import {
  deleteJournalAttachment,
  updateJournalAttachment,
  deleteJournalEntry,
  fetchJournal,
  uploadJournalAttachment,
  upsertJournalEntry,
} from "@/state/slices/journalSlice";
import {
  clearJournalFocus,
  setImageBoardOpen,
  setJournalOpen,
  setPriceRange,
  setShowHistoryOrders,
  setSymbol,
  setViewport,
} from "@/state/slices/uiSlice";
import { captureChart, panChartTo } from "@/utils/chartCapture";
import { tfMt5Label, tfSeconds } from "@/utils/timeframe";

// How many bars the jump target should keep visible. Mirrors
// Chart.tsx's INITIAL_VISIBLE_BARS so a jumped-to chart looks like
// a freshly-opened chart at the same TF — same candle pixel
// width, same density. Keeping these in sync by convention; if
// Chart.tsx ever changes its initial-bar count, update here too.
const JUMP_VISIBLE_BARS = 250;
import {
  groupDealsToRoundTrips,
  type RoundTrip,
} from "@/components/PositionsPanel/PositionsPanel";
import type {
  JournalAttachment,
  JournalEntry,
  TradingOrder,
  TradingPosition,
} from "@/api/types";
import { apiUrl } from "@/api/client";
import { fmtShortDateTime } from "@/utils/chartTime";
import { AttachmentThumb } from "@/components/AttachmentThumb/AttachmentThumb";
import {
  LinkedTextArea,
  RefText,
  type LinkedTextAreaHandle,
} from "@/components/RefText/RefText";
import { ImageEditor } from "@/components/ImageEditor/ImageEditor";
import {
  ImageLightbox,
  type GalleryImage,
} from "@/components/ImageLightbox/ImageLightbox";
import styles from "./TradeJournal.module.css";

/** Display label for an attachment.
 *
 *  Falls back to the position for anything the server backfill
 *  hasn't reached yet — it runs on the list endpoint, so in practice
 *  only an attachment uploaded by an older build during this very
 *  session can be missing one. */
export function journalLabelFor(
  att: JournalAttachment,
  index: number,
): string {
  return att.label || String(index + 1);
}

type SectionId = "open" | "pending" | "closed" | "hidden";

const SECTION_LABELS: Record<SectionId, string> = {
  open: "Open Positions",
  pending: "Pending Orders",
  closed: "Closed Trades",
  hidden: "Hidden",
};

/** Image-board icon — a framed picture, matching the Notes drawer's
 *  button so the same action reads the same in both headers. */
function ImageBoardIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      aria-hidden
    >
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path
        d="M3 15l5-5 4 4 3-3 6 6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="9" cy="9" r="1.4" />
    </svg>
  );
}

/** "Collapse all" icon — material-style `unfold_less`: two chevrons
 *  pointing inward toward a horizontal centre line. Reads as "fold
 *  the expanded items back together" at any size. */
const CollapseAllIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden>
    <path
      d="M4 3 L8 6 L12 3"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <line
      x1="3"
      y1="8"
      x2="13"
      y2="8"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M4 13 L8 10 L12 13"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Refresh icon — circular arrow. Replaces the bare "↻" glyph
 *  which renders inconsistently across fonts. */
const RefreshIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden>
    <path
      d="M13 8 A5 5 0 1 1 11.5 4.5"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
    />
    <path
      d="M13 3 V5 H11"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Jump-to-trade icon — a chart line with a target arrow. Used on
 *  closed-trade rows to mean "pan the chart so this trade is
 *  visible". An external-link / arrow-out style reads as "go to"
 *  better than a generic chevron. */
const JumpIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden>
    <path
      d="M9 3 H13 V7"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <line
      x1="13"
      y1="3"
      x2="7.5"
      y2="8.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M3 8 V13 H11"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Close icon — clean diagonal X. Same reason as Refresh: the
 *  text glyph ✕ varies a lot across fonts. */
const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden>
    <line
      x1="4"
      y1="4"
      x2="12"
      y2="12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <line
      x1="12"
      y1="4"
      x2="4"
      y2="12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

function fmtPrice(p: number, decimals = 2): string {
  if (!Number.isFinite(p)) return "—";
  return p.toFixed(decimals);
}

// Trade times come from MT5 (UTC). Render in the chart display zone (UTC)
// so a deal reads the same as the candle it sits on. See chartTime.ts.
const fmtTime = fmtShortDateTime;

function fmtMoney(n: number, currency = "USD"): string {
  const sign = n >= 0 ? "" : "-";
  return `${sign}${currency === "USD" ? "$" : ""}${Math.abs(n).toFixed(2)}`;
}

function profitClass(n: number): string {
  if (n > 0) return styles.profitPos;
  if (n < 0) return styles.profitNeg;
  return styles.profitNeutral;
}

export function TradeJournal() {
  const dispatch = useAppDispatch();
  const canJournal = useCan(CAP.journalRead);
  const open = useAppSelector((s) => s.ui.journalOpen);
  const positions = useAppSelector((s) => s.trading.positions);
  const orders = useAppSelector((s) => s.trading.orders);
  const history = useAppSelector((s) => s.trading.history);
  const historyStatus = useAppSelector((s) => s.trading.historyStatus);
  const journalByKey = useAppSelector((s) => s.journal.byKey);
  const journalStatus = useAppSelector((s) => s.journal.status);
  const accountCurrency = useAppSelector(
    (s) => s.trading.account?.currency ?? "USD",
  );

  // Open/close state of the three accordion sections. All open by
  // default — when the drawer first appears the user sees
  // everything; collapsing is a per-session preference and isn't
  // persisted across reloads.
  const [collapsed, setCollapsed] = useState<Record<SectionId, boolean>>({
    open: false,
    pending: false,
    closed: false,
    // Hidden defaults to collapsed — it's an archive, not the
    // primary view. User expands when they want to revert something.
    hidden: true,
  });

  // Per-entry expanded state lives here (not in EntryRow) so the
  // drawer header's "Collapse all" can clear it in one go. Keys
  // are journal keys (`pos:N` / `pending:N`); presence in the set
  // means the entry is expanded. Default: empty (all collapsed)
  // so the drawer first opens as a scannable summary list, not a
  // wall of textareas.
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const toggleExpanded = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const collapseAll = () => setExpandedKeys(new Set());
  const anyExpanded = expandedKeys.size > 0;

  // One-shot "jump to this entry" request parked by the Image Board.
  const focusJournalKey = useAppSelector((s) => s.ui.focusJournalKey);

  // Consume the jump: expand the entry, un-collapse every section
  // (we don't know which one holds it without re-deriving the whole
  // partition, and opening all four is harmless), scroll it in.
  useEffect(() => {
    if (!open || !focusJournalKey) return;
    setExpandedKeys((prev) => new Set(prev).add(focusJournalKey));
    setCollapsed({
      open: false,
      pending: false,
      closed: false,
      hidden: false,
    });
    // Two frames: one for the section to expand, one for the row to
    // take its expanded height. Scrolling earlier lands short.
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        // Found by data attribute rather than a ref: EntryRow is
        // rendered from four different wrappers and threading a ref
        // callback through all of them to reach one row is more
        // machinery than the lookup is worth.
        document
          .querySelector(`[data-journal-key="${focusJournalKey}"]`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    });
    dispatch(clearJournalFocus());
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [open, focusJournalKey, dispatch]);

  /** Collapse just the entries whose keys are in `keys`. Lets each
   *  section header offer a localised collapse without touching the
   *  other two sections' expanded state. */
  const collapseSection = (keys: string[]) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      for (const k of keys) next.delete(k);
      return next;
    });
  };

  const roundTrips = useMemo<RoundTrip[]>(
    () => groupDealsToRoundTrips(history.deals),
    [history.deals],
  );

  // Terminal-no-fill orders from history: pending orders the user
  // cancelled, or that the broker expired / rejected. They never
  // became a position so they don't appear in roundTrips; surface
  // them in the closed section so any journal note written during
  // the pending phase (key `pending:<ticket>`) stays accessible.
  const closedOrders = useMemo<TradingOrder[]>(
    () =>
      history.orders.filter((o) => {
        const s = o.state_label;
        return (
          s === "canceled" || s === "expired" || s === "rejected"
        );
      }),
    [history.orders],
  );

  // Closed section combines real round-trips (deals grouped by
  // position_id) with the cancelled / expired / rejected orders,
  // sorted by resolution time desc — so the most recently-resolved
  // item (whether it filled or cancelled) sits at the top.
  //
  // Cancelled-order rows can be soft-hidden via the row's Hide
  // button (sets journal entry `hidden: true`); those move into
  // the Hidden section instead. Round-trips are never hideable —
  // they represent real money flow, not iteration noise.
  type ClosedItem =
    | { kind: "trip"; sortKey: number; trip: RoundTrip }
    | { kind: "order"; sortKey: number; order: TradingOrder };
  const allClosedItems = useMemo<ClosedItem[]>(() => {
    const tripItems: ClosedItem[] = roundTrips.map((t) => ({
      kind: "trip",
      sortKey: t.exitTime,
      trip: t,
    }));
    const orderItems: ClosedItem[] = closedOrders.map((o) => ({
      kind: "order",
      sortKey: o.time_done || o.time_setup,
      order: o,
    }));
    return [...tripItems, ...orderItems].sort(
      (a, b) => b.sortKey - a.sortKey,
    );
  }, [roundTrips, closedOrders]);

  // Split: visible (Closed section) vs hidden (Hidden section).
  // Round-trips are always visible; cancelled orders flip based on
  // their journal entry's `hidden` flag.
  const closedItems = useMemo<ClosedItem[]>(
    () =>
      allClosedItems.filter((it) => {
        if (it.kind === "trip") return true;
        const e = journalByKey[`pending:${it.order.ticket}`];
        return !e?.hidden;
      }),
    [allClosedItems, journalByKey],
  );
  const hiddenItems = useMemo<ClosedItem[]>(
    () =>
      allClosedItems.filter((it) => {
        if (it.kind === "trip") return false;
        const e = journalByKey[`pending:${it.order.ticket}`];
        return !!e?.hidden;
      }),
    [allClosedItems, journalByKey],
  );

  // Precompute the journal keys each section is responsible for so
  // the section-level "collapse all" knows which entries to clear.
  const openKeys = useMemo(
    () => positions.map((p) => `pos:${p.identifier || p.ticket}`),
    [positions],
  );
  const pendingKeys = useMemo(
    () => orders.map((o) => `pending:${o.ticket}`),
    [orders],
  );
  const closedKeys = useMemo(
    () =>
      closedItems.map((it) =>
        it.kind === "trip"
          ? `pos:${it.trip.positionId}`
          : `pending:${it.order.ticket}`,
      ),
    [closedItems],
  );
  const hiddenKeys = useMemo(
    () => hiddenItems.map((it) => `pending:${(it as { order: TradingOrder }).order.ticket}`),
    [hiddenItems],
  );
  const anyExpandedIn = (keys: string[]) =>
    keys.some((k) => expandedKeys.has(k));

  // Fetch on first open: journal entries (full bulk load) + all
  // cached the trading server history (delta-synced server-side on every call).
  // Re-fetches each time the drawer opens so the Closed list
  // reflects any trades that closed while the drawer was hidden.
  useEffect(() => {
    if (!open || !canJournal) return;
    if (journalStatus === "idle") {
      void dispatch(fetchJournal());
    }
    void dispatch(fetchTradingHistory());
  }, [dispatch, open, canJournal, journalStatus]);

  // Close the drawer on Escape. An attachment lightbox, if open,
  // handles Escape first in the capture phase and stops propagation,
  // so this only fires when no image is being viewed.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dispatch(setJournalOpen(false));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, dispatch]);

  if (!canJournal || !open) return null;

  // Summary stats from the closed round-trips (cancelled orders don't
  // count as trades). Net P&L, win-rate %, and the trade count.
  let netPnl = 0;
  let wins = 0;
  let tradeCount = 0;
  for (const it of closedItems) {
    if (it.kind !== "trip") continue;
    tradeCount += 1;
    netPnl += it.trip.net ?? 0;
    if (it.trip.net > 0) wins += 1;
  }
  const winRate =
    tradeCount > 0 ? Math.round((wins / tradeCount) * 100) : 0;

  return (
    <aside className={styles.drawer} aria-label="Trade journal">
      <div className={styles.header}>
        <div className={styles.title}>
          <span className={styles.titleIco}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
              <path d="M4 5h16M4 12h16M4 19h10" strokeLinecap="round" />
            </svg>
          </span>
          Trade Journal
        </div>
        <button
          type="button"
          className={styles.iconBtn}
          title="Browse every image from your notes and trades"
          aria-label="Open image board"
          onClick={() => dispatch(setImageBoardOpen(true))}
        >
          <ImageBoardIcon />
        </button>
        <button
          type="button"
          className={styles.iconBtn}
          title="Collapse all open notes"
          onClick={collapseAll}
          disabled={!anyExpanded}
        >
          <CollapseAllIcon />
        </button>
        <button
          type="button"
          className={styles.iconBtn}
          title="Refresh history"
          onClick={() => void dispatch(fetchTradingHistory())}
        >
          <RefreshIcon />
        </button>
        <button
          type="button"
          className={styles.iconBtn}
          title="Close"
          onClick={() => dispatch(setJournalOpen(false))}
        >
          <CloseIcon />
        </button>
      </div>

      <div className={styles.summary}>
        <div className={styles.stat}>
          <div className={styles.statLbl}>Net P&amp;L</div>
          <div className={`${styles.statVal} ${profitClass(netPnl)}`}>
            {fmtMoney(netPnl, accountCurrency)}
          </div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statLbl}>Win rate</div>
          <div className={styles.statVal}>{winRate}%</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statLbl}>Trades</div>
          <div className={styles.statVal}>{tradeCount}</div>
        </div>
      </div>

      <div className={styles.body}>
        <Section
          id="open"
          label={SECTION_LABELS.open}
          count={positions.length}
          collapsed={collapsed.open}
          onToggle={() =>
            setCollapsed((c) => ({ ...c, open: !c.open }))
          }
          anyExpanded={anyExpandedIn(openKeys)}
          onCollapseAll={() => collapseSection(openKeys)}
        >
          {positions.length === 0 ? (
            <div className={styles.empty}>No open positions.</div>
          ) : (
            positions.map((p) => {
              const k = `pos:${p.identifier || p.ticket}`;
              return (
                <PositionEntry
                  key={p.ticket}
                  position={p}
                  entry={journalByKey[k]}
                  accountCurrency={accountCurrency}
                  expanded={expandedKeys.has(k)}
                  onToggle={() => toggleExpanded(k)}
                />
              );
            })
          )}
        </Section>

        <Section
          id="pending"
          label={SECTION_LABELS.pending}
          count={orders.length}
          collapsed={collapsed.pending}
          onToggle={() =>
            setCollapsed((c) => ({ ...c, pending: !c.pending }))
          }
          anyExpanded={anyExpandedIn(pendingKeys)}
          onCollapseAll={() => collapseSection(pendingKeys)}
        >
          {orders.length === 0 ? (
            <div className={styles.empty}>No pending orders.</div>
          ) : (
            orders.map((o) => {
              const k = `pending:${o.ticket}`;
              return (
                <OrderEntry
                  key={o.ticket}
                  order={o}
                  entry={journalByKey[k]}
                  expanded={expandedKeys.has(k)}
                  onToggle={() => toggleExpanded(k)}
                />
              );
            })
          )}
        </Section>

        <Section
          id="closed"
          label={SECTION_LABELS.closed}
          count={closedItems.length}
          collapsed={collapsed.closed}
          onToggle={() =>
            setCollapsed((c) => ({ ...c, closed: !c.closed }))
          }
          anyExpanded={anyExpandedIn(closedKeys)}
          onCollapseAll={() => collapseSection(closedKeys)}
        >
          {historyStatus === "loading" && closedItems.length === 0 ? (
            <div className={styles.loading}>Loading history…</div>
          ) : closedItems.length === 0 ? (
            <div className={styles.empty}>
              No closed trades or cancelled orders yet.
            </div>
          ) : (
            closedItems.map((it) => {
              if (it.kind === "trip") {
                const k = `pos:${it.trip.positionId}`;
                return (
                  <RoundTripEntry
                    key={`trip-${it.trip.positionId}`}
                    trip={it.trip}
                    entry={journalByKey[k]}
                    accountCurrency={accountCurrency}
                    expanded={expandedKeys.has(k)}
                    onToggle={() => toggleExpanded(k)}
                  />
                );
              }
              const k = `pending:${it.order.ticket}`;
              return (
                <CancelledOrderEntry
                  key={`order-${it.order.ticket}`}
                  order={it.order}
                  entry={journalByKey[k]}
                  expanded={expandedKeys.has(k)}
                  onToggle={() => toggleExpanded(k)}
                  isHidden={false}
                />
              );
            })
          )}
        </Section>

        <Section
          id="hidden"
          label={SECTION_LABELS.hidden}
          count={hiddenItems.length}
          collapsed={collapsed.hidden}
          onToggle={() =>
            setCollapsed((c) => ({ ...c, hidden: !c.hidden }))
          }
          anyExpanded={anyExpandedIn(hiddenKeys)}
          onCollapseAll={() => collapseSection(hiddenKeys)}
        >
          {hiddenItems.length === 0 ? (
            <div className={styles.empty}>
              Nothing hidden. Use the Hide button on a cancelled
              order to move it here.
            </div>
          ) : (
            hiddenItems.map((it) => {
              // hiddenItems is filtered to "order" kind only.
              if (it.kind !== "order") return null;
              const k = `pending:${it.order.ticket}`;
              return (
                <CancelledOrderEntry
                  key={`hidden-${it.order.ticket}`}
                  order={it.order}
                  entry={journalByKey[k]}
                  expanded={expandedKeys.has(k)}
                  onToggle={() => toggleExpanded(k)}
                  isHidden={true}
                />
              );
            })
          )}
        </Section>
      </div>
    </aside>
  );
}

// -----------------------------------------------------------------
// Section accordion
// -----------------------------------------------------------------

function Section({
  id,
  label,
  count,
  collapsed,
  onToggle,
  anyExpanded,
  onCollapseAll,
  children,
}: {
  id: SectionId;
  label: string;
  count: number;
  collapsed: boolean;
  onToggle: () => void;
  /** True if any entry in this section is currently expanded — the
   *  per-section collapse button is hidden otherwise to avoid
   *  cluttering headers with no work to do. */
  anyExpanded: boolean;
  onCollapseAll: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.section}>
      <div
        className={styles.sectionHeader}
        onClick={onToggle}
        role="button"
        aria-expanded={!collapsed}
        aria-controls={`section-${id}`}
      >
        <span className={styles.chevron}>{collapsed ? "▶" : "▼"}</span>
        <span className={styles.sectionHeaderLabel}>{label}</span>
        {anyExpanded && (
          <button
            type="button"
            className={styles.sectionCollapseBtn}
            title={`Collapse all open notes in ${label}`}
            onClick={(e) => {
              // Stop the click from also toggling the section
              // open/closed — they're different intents.
              e.stopPropagation();
              onCollapseAll();
            }}
          >
            <CollapseAllIcon />
          </button>
        )}
        <span
          className={`${styles.sectionCount} ${count > 0 ? styles.sectionCountHas : ""}`}
        >
          {count}
        </span>
      </div>
      {!collapsed && (
        <div className={styles.sectionBody} id={`section-${id}`}>
          {children}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------
// Open position row
// -----------------------------------------------------------------

function PositionEntry({
  position,
  entry,
  accountCurrency,
  expanded,
  onToggle,
}: {
  position: TradingPosition;
  entry: JournalEntry | undefined;
  accountCurrency: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const key = `pos:${position.identifier || position.ticket}`;
  const isBuy = position.type_label === "buy";
  return (
    <EntryRow
      journalKey={key}
      entry={entry}
      expanded={expanded}
      onToggle={onToggle}
      side={isBuy ? "buy" : "sell"}
      summary={
        <>
          <div className={styles.entrySymbol}>
            <span>{position.symbol}</span>
            <span
              className={`${styles.dirBadge} ${isBuy ? styles.dirBuy : styles.dirSell}`}
            >
              {position.type_label.toUpperCase()} {position.volume}
            </span>
          </div>
          <div
            className={`${styles.entryProfit} ${profitClass(position.profit + position.swap)}`}
          >
            {fmtMoney(position.profit + position.swap, accountCurrency)}
          </div>
          <div className={styles.entryMeta}>
            <span>
              <span className={styles.metaLabel}>Entry </span>
              <span className={styles.metaValue}>
                {fmtPrice(position.price_open, 3)}
              </span>
            </span>
            <span>
              <span className={styles.metaLabel}>Now </span>
              <span className={styles.metaValue}>
                {fmtPrice(position.price_current, 3)}
              </span>
            </span>
            {position.sl > 0 && (
              <span>
                <span className={styles.metaLabel}>SL </span>
                <span className={styles.metaValue}>
                  {fmtPrice(position.sl, 3)}
                </span>
              </span>
            )}
            {position.tp > 0 && (
              <span>
                <span className={styles.metaLabel}>TP </span>
                <span className={styles.metaValue}>
                  {fmtPrice(position.tp, 3)}
                </span>
              </span>
            )}
            <span>
              <span className={styles.metaLabel}>Opened </span>
              <span className={styles.metaValue}>
                {fmtTime(position.time)}
              </span>
            </span>
          </div>
          {entry?.reason && (
            <div className={styles.reasonPreview}>
              “<RefText text={entry.reason} />”
            </div>
          )}
        </>
      }
    />
  );
}

// -----------------------------------------------------------------
// Pending order row
// -----------------------------------------------------------------

function OrderEntry({
  order,
  entry,
  expanded,
  onToggle,
}: {
  order: TradingOrder;
  entry: JournalEntry | undefined;
  expanded: boolean;
  onToggle: () => void;
}) {
  const key = `pending:${order.ticket}`;
  return (
    <EntryRow
      journalKey={key}
      entry={entry}
      expanded={expanded}
      onToggle={onToggle}
      side={order.type_label?.toLowerCase().includes("buy") ? "buy" : "sell"}
      summary={
        <>
          <div className={styles.entrySymbol}>
            <span>{order.symbol}</span>
            <span className={`${styles.dirBadge} ${styles.dirPending}`}>
              {order.type_label.toUpperCase()} {order.volume_current}
            </span>
          </div>
          <div className={`${styles.entryProfit} ${styles.profitNeutral}`}>
            —
          </div>
          <div className={styles.entryMeta}>
            <span>
              <span className={styles.metaLabel}>Trigger </span>
              <span className={styles.metaValue}>
                {fmtPrice(order.price_open, 3)}
              </span>
            </span>
            <span>
              <span className={styles.metaLabel}>Now </span>
              <span className={styles.metaValue}>
                {fmtPrice(order.price_current, 3)}
              </span>
            </span>
            {order.sl > 0 && (
              <span>
                <span className={styles.metaLabel}>SL </span>
                <span className={styles.metaValue}>
                  {fmtPrice(order.sl, 3)}
                </span>
              </span>
            )}
            {order.tp > 0 && (
              <span>
                <span className={styles.metaLabel}>TP </span>
                <span className={styles.metaValue}>
                  {fmtPrice(order.tp, 3)}
                </span>
              </span>
            )}
            <span>
              <span className={styles.metaLabel}>Set </span>
              <span className={styles.metaValue}>
                {fmtTime(order.time_setup)}
              </span>
            </span>
          </div>
          {entry?.reason && (
            <div className={styles.reasonPreview}>
              “<RefText text={entry.reason} />”
            </div>
          )}
        </>
      }
    />
  );
}

// -----------------------------------------------------------------
// Cancelled / expired / rejected pending-order row
// -----------------------------------------------------------------
//
// Lives in the Closed section so the user's journal note from the
// pending phase (key `pending:<ticket>`) survives even though the
// order never became a position. Visually similar to OrderEntry
// but with a CANCELLED badge + the resolution timestamp.

function CancelledOrderEntry({
  order,
  entry,
  expanded,
  onToggle,
  isHidden,
}: {
  order: TradingOrder;
  entry: JournalEntry | undefined;
  expanded: boolean;
  onToggle: () => void;
  /** True when rendered inside the Hidden section. Flips the
   *  row's action button between "Hide" (in Closed) and
   *  "Revert" (in Hidden). */
  isHidden: boolean;
}) {
  const dispatch = useAppDispatch();
  const key = `pending:${order.ticket}`;
  const stateLabel = (order.state_label ?? "cancelled").toUpperCase();
  // Hide / Revert just toggle the journal entry's `hidden` flag —
  // the partition above re-derives which section the row lives in.
  // Upsert with no reason/notes preserves whatever the user has
  // already typed; the backend patches only the fields supplied.
  const onToggleHidden = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(
      upsertJournalEntry({ key, hidden: !isHidden }),
    );
  };
  return (
    <EntryRow
      journalKey={key}
      entry={entry}
      expanded={expanded}
      onToggle={onToggle}
      side={order.type_label?.toLowerCase().includes("buy") ? "buy" : "sell"}
      summary={
        <>
          <div className={styles.entrySymbol}>
            <span>{order.symbol}</span>
            <span className={`${styles.dirBadge} ${styles.dirPending}`}>
              {order.type_label.toUpperCase()} {order.volume_initial}
            </span>
            <span
              className={`${styles.dirBadge} ${styles.dirCancelled}`}
              title="This pending order didn't fill"
            >
              {stateLabel}
            </span>
            <button
              type="button"
              className={styles.rowAction}
              onClick={onToggleHidden}
              title={
                isHidden
                  ? "Move back to Closed Trades"
                  : "Hide from Closed Trades (keeps the journal note)"
              }
            >
              {isHidden ? "Revert" : "Hide"}
            </button>
          </div>
          <div className={`${styles.entryProfit} ${styles.profitNeutral}`}>
            —
          </div>
          <div className={styles.entryMeta}>
            <span>
              <span className={styles.metaLabel}>Trigger </span>
              <span className={styles.metaValue}>
                {fmtPrice(order.price_open, 3)}
              </span>
            </span>
            {order.sl > 0 && (
              <span>
                <span className={styles.metaLabel}>SL </span>
                <span className={styles.metaValue}>
                  {fmtPrice(order.sl, 3)}
                </span>
              </span>
            )}
            {order.tp > 0 && (
              <span>
                <span className={styles.metaLabel}>TP </span>
                <span className={styles.metaValue}>
                  {fmtPrice(order.tp, 3)}
                </span>
              </span>
            )}
            <span>
              <span className={styles.metaLabel}>Set </span>
              <span className={styles.metaValue}>
                {fmtTime(order.time_setup)}
              </span>
            </span>
            {(order.time_done ?? 0) > 0 && (
              <span>
                <span className={styles.metaLabel}>Resolved </span>
                <span className={styles.metaValue}>
                  {fmtTime(order.time_done!)}
                </span>
              </span>
            )}
          </div>
          {entry?.reason && (
            <div className={styles.reasonPreview}>
              “<RefText text={entry.reason} />”
            </div>
          )}
        </>
      }
    />
  );
}

// -----------------------------------------------------------------
// Closed round-trip row
// -----------------------------------------------------------------

function RoundTripEntry({
  trip,
  entry,
  accountCurrency,
  expanded,
  onToggle,
}: {
  trip: RoundTrip;
  entry: JournalEntry | undefined;
  accountCurrency: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const dispatch = useAppDispatch();
  const activeTabId = useActiveTabId();
  const activeTabUi = useAppSelector((s) => s.ui.byTab[activeTabId]);
  const activeSymbol = activeTabUi?.symbol;
  const activeTimeframe = activeTabUi?.timeframe ?? "1H";
  const activeShowHistoryOrders =
    activeTabUi?.showHistoryOrders ?? false;
  const key = `pos:${trip.positionId}`;
  const isBuy = trip.type === "buy";

  /** Pan the focused chart so the entry + exit are visible, sized
   *  to keep candles at their normal pixel width.
   *
   *  Window sizing:
   *    - Compute a target span = JUMP_VISIBLE_BARS * tfSeconds
   *      so e.g. a 1H jump always shows ~60 hours of price action.
   *    - If the trade itself is wider than that target, expand to
   *      fit the trade + ~30% padding on each side.
   *  Centring: midpoint of (entry, exit), so a long swing trade
   *  sits in the middle of the view rather than hugging one edge.
   *
   *  Same-symbol jumps go through the chartPan registry for
   *  instant feedback. Cross-symbol jumps dispatch setViewport +
   *  setSymbol — the chart's data-refetch + fresh-load path
   *  applies the saved viewport once the new symbol's candles
   *  arrive. Also auto-enables showHistoryOrders so the markers
   *  are actually visible at the jump target; the user can toggle
   *  off later via the chart's right-click menu. */
  const jumpToTrade = (e: React.MouseEvent) => {
    e.stopPropagation();
    const tfSec = tfSeconds(activeTimeframe);
    const desiredSpan = JUMP_VISIBLE_BARS * tfSec;
    const tradeSpan = Math.max(trip.exitTime - trip.entryTime, tfSec);
    const totalSpan = Math.max(desiredSpan, tradeSpan * 1.6);
    const midPoint = (trip.entryTime + trip.exitTime) / 2;
    const from = midPoint - totalSpan / 2;
    const to = midPoint + totalSpan / 2;

    if (!activeShowHistoryOrders) {
      dispatch(
        setShowHistoryOrders({ tabId: activeTabId, show: true }),
      );
    }
    dispatch(
      setViewport({ tabId: activeTabId, viewport: { from, to } }),
    );
    // Release any saved price-range lock so the price axis re-fits
    // to the historical bars at the jump target instead of staying
    // glued to "current price". The chart's pan handler also drops
    // its autoscaleInfoProvider on same-symbol jumps; this dispatch
    // makes the cross-symbol path (data refetch → fresh-load) see
    // priceRange: null too.
    dispatch(
      setPriceRange({ tabId: activeTabId, priceRange: null }),
    );
    if (activeSymbol === trip.symbol) {
      panChartTo(activeTabId, from, to);
    } else {
      dispatch(setSymbol({ tabId: activeTabId, symbol: trip.symbol }));
    }
  };

  return (
    <EntryRow
      journalKey={key}
      entry={entry}
      expanded={expanded}
      onToggle={onToggle}
      side={isBuy ? "buy" : "sell"}
      summary={
        <>
          <div className={styles.entrySymbol}>
            <button
              type="button"
              className={styles.jumpBtn}
              onClick={jumpToTrade}
              title={`Jump chart to ${trip.symbol} ${fmtTime(trip.entryTime)} → ${fmtTime(trip.exitTime)}`}
            >
              <JumpIcon />
            </button>
            <span>{trip.symbol}</span>
            <span
              className={`${styles.dirBadge} ${isBuy ? styles.dirBuy : styles.dirSell}`}
            >
              {trip.type.toUpperCase()} {trip.volume}
            </span>
          </div>
          <div className={`${styles.entryProfit} ${profitClass(trip.net)}`}>
            {fmtMoney(trip.net, accountCurrency)}
          </div>
          <div className={styles.entryMeta}>
            <span>
              <span className={styles.metaLabel}>In </span>
              <span className={styles.metaValue}>
                {fmtPrice(trip.entryPrice, 3)}
              </span>
              <span className={styles.metaLabel}> · {fmtTime(trip.entryTime)}</span>
            </span>
            <span>
              <span className={styles.metaLabel}>Out </span>
              <span className={styles.metaValue}>
                {fmtPrice(trip.exitPrice, 3)}
              </span>
              <span className={styles.metaLabel}> · {fmtTime(trip.exitTime)}</span>
            </span>
          </div>
          {entry?.reason && (
            <div className={styles.reasonPreview}>
              “<RefText text={entry.reason} />”
            </div>
          )}
        </>
      }
    />
  );
}

// -----------------------------------------------------------------
// Shared expandable row with reason editor + attachments
// -----------------------------------------------------------------

function EntryRow({
  journalKey,
  entry,
  expanded,
  onToggle,
  summary,
  side,
}: {
  journalKey: string;
  entry: JournalEntry | undefined;
  expanded: boolean;
  onToggle: () => void;
  summary: React.ReactNode;
  /** Trade direction → colors the card's left edge. */
  side?: "buy" | "sell";
}) {
  const dispatch = useAppDispatch();
  const activeTabId = useActiveTabId();
  // Timeframe of the chart a capture would come from, in MT5
  // spelling — becomes the label's prefix ("H1-2"). Read here rather
  // than passed down so it is current at the moment of capture.
  const activeTf = useAppSelector(
    (s) => s.ui.byTab[activeTabId]?.timeframe,
  );
  const capturePrefix = activeTf ? tfMt5Label(activeTf) : undefined;
  const [reason, setReason] = useState(entry?.reason ?? "");
  // Tracks the last value we successfully persisted so we can render
  // "unsaved changes" vs "saved" without comparing to a stale entry
  // ref (the slice updates `entry` on save but local typing can run
  // ahead of that).
  const [savedReason, setSavedReason] = useState(entry?.reason ?? "");
  const [saving, setSaving] = useState(false);
  // Index into this entry's attachments, or null when closed. An
  // index rather than an id so the gallery can walk the set.
  const [lightbox, setLightbox] = useState<number | null>(null);
  // Attachment open in the annotator, or null.
  const [editing, setEditing] = useState<{
    attId: string;
    url: string;
    filename: string;
    label: string;
  } | null>(null);
  // Lets the "+#" button drop a reference at the caret.
  const reasonRef = useRef<LinkedTextAreaHandle>(null);

  /** Drop `[#H1-2]` into the reason at the caret — the same
   *  affordance the Notes drawer has, for the same reason: you can
   *  see the thumbnail while writing but not what to call it. */
  const insertRef = (label: string) => {
    reasonRef.current?.insertAtCaret(`[#${label}]`);
  };

  /** Open the image a `[#H1-2]` in the reason points at. */
  const openRef = (label: string) => {
    const idx = attachments.findIndex(
      (a, i) => journalLabelFor(a, i) === label,
    );
    if (idx >= 0) setLightbox(idx);
  };

  // Keep local state in sync with server data when it arrives
  // (initial fetch, or another window saved). Only override if the
  // local buffer was untouched since the last save — that way mid-
  // typing edits aren't clobbered.
  useEffect(() => {
    if (!entry) return;
    if (reason === savedReason && entry.reason !== savedReason) {
      setReason(entry.reason);
      setSavedReason(entry.reason);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry?.reason]);

  const dirty = reason !== savedReason;

  const persistReason = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await dispatch(
        upsertJournalEntry({ key: journalKey, reason }),
      ).unwrap();
      setSavedReason(reason);
    } finally {
      setSaving(false);
    }
  };

  /** Commit point for the editor: blur, and Ctrl/Cmd+Enter — the
   *  common journal shortcut, so a user writing doesn't have to
   *  mouse over to the button. Also the safety net for someone who
   *  tabs away or collapses the row mid-edit. No-op if not dirty. */
  const onBlur = () => {
    if (dirty) void persistReason();
  };

  const captureCurrentChart = async () => {
    const result = await captureChart(activeTabId);
    if (!result) return;
    void dispatch(
      uploadJournalAttachment({
        key: journalKey,
        blob: result.blob,
        filename: result.filename,
        prefix: capturePrefix,
      }),
    );
  };

  /** Disk upload and clipboard paste both land here. Neither sends a
   *  prefix: the image didn't come from a chart, so labelling it with
   *  whatever timeframe happens to be on screen would be a lie. The
   *  server applies its generic prefix instead. */
  const uploadFile = (file: File) => {
    void dispatch(
      uploadJournalAttachment({
        key: journalKey,
        blob: file,
        filename: file.name,
      }),
    );
  };

  const onPickFile = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/png";
    input.onchange = () => {
      const f = input.files?.[0];
      if (f) uploadFile(f);
    };
    input.click();
  };

  const onPaste = (e: React.ClipboardEvent) => {
    const item = Array.from(e.clipboardData.items).find((i) =>
      i.type.startsWith("image/"),
    );
    if (!item) return;
    const file = item.getAsFile();
    if (file) uploadFile(file);
  };

  const deleteAttachment = (att: JournalAttachment) => {
    void dispatch(
      deleteJournalAttachment({ key: journalKey, attId: att.id }),
    );
  };

  const renameAttachment = (att: JournalAttachment, next: string) => {
    void dispatch(
      updateJournalAttachment({
        key: journalKey,
        attId: att.id,
        patch: { label: next },
      }),
    );
  };

  /** Commit an annotated image — upload the new one, then drop the
   *  original. Same order and same reasoning as the Notes drawer:
   *  the blob endpoint is cached, and uploading first means a failed
   *  save leaves the original intact. The original's label rides
   *  along so a note citing "#2" still matches its picture. */
  const saveEditedAttachment = async (blob: Blob, filename: string) => {
    if (!editing) return;
    await dispatch(
      uploadJournalAttachment({
        key: journalKey,
        blob,
        filename,
        label: editing.label,
      }),
    ).unwrap();
    await dispatch(
      deleteJournalAttachment({ key: journalKey, attId: editing.attId }),
    ).unwrap();
    setEditing(null);
  };

  const attachments = entry?.attachments ?? [];
  const gallery: GalleryImage[] = attachments.map((att, i) => ({
    id: att.id,
    url: apiUrl(`/api/journal/attachments/${att.id}`),
    label: journalLabelFor(att, i),
    filename: att.filename,
    context: entry?.reason?.split("\n")[0] || null,
  }));

  const removeEntry = () => {
    if (!entry) return;
    if (
      !window.confirm(
        "Delete this journal note and all attachments? This only removes your local notes — the trade itself is unaffected.",
      )
    ) {
      return;
    }
    void dispatch(deleteJournalEntry(journalKey));
  };

  return (
    <div
      // How the Image Board's "Jump to trade" finds this row to
      // scroll it into view — see the focusJournalKey effect.
      data-journal-key={journalKey}
      className={`${styles.entry} ${
        side === "buy"
          ? styles.entryBuy
          : side === "sell"
            ? styles.entrySell
            : ""
      }`}
    >
      <div className={styles.entrySummary} onClick={onToggle}>
        {summary}
      </div>
      {expanded && (
        <div className={styles.entryExpanded} onPaste={onPaste}>
          <div className={styles.fieldLabelRow}>
            <span className={styles.fieldLabel}>Reason / playbook</span>
            <span
              className={`${styles.saveStatus} ${
                saving
                  ? styles.saveStatusSaving
                  : dirty
                    ? styles.saveStatusDirty
                    : entry
                      ? styles.saveStatusSaved
                      : ""
              }`}
            >
              {saving
                ? "Saving…"
                : dirty
                  ? "● Unsaved"
                  : entry
                    ? "✓ Saved"
                    : ""}
            </span>
          </div>
          <LinkedTextArea
            handleRef={reasonRef}
            className={styles.textarea}
            placeholder="Why are you in this trade? (Ctrl+Enter to save)"
            value={reason}
            onChange={setReason}
            onCommit={onBlur}
            onRefClick={openRef}
          />
          <div className={styles.actionRow}>
            <button
              type="button"
              className={styles.actionPrimary}
              onClick={() => void persistReason()}
              disabled={!dirty || saving}
              title={
                dirty
                  ? "Save reason (Ctrl+Enter)"
                  : "No changes to save"
              }
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>

          <div className={styles.fieldLabel}>Analysis screenshots</div>
          <div className={styles.attachmentsRow}>
            {attachments.map((att, i) => {
              const label = journalLabelFor(att, i);
              const url = apiUrl(`/api/journal/attachments/${att.id}`);
              return (
                <AttachmentThumb
                  key={att.id}
                  url={url}
                  filename={att.filename}
                  bytes={att.bytes}
                  label={label}
                  citedIn="the reason"
                  onOpen={() => setLightbox(i)}
                  onAnnotate={() =>
                    setEditing({
                      attId: att.id,
                      url,
                      filename: att.filename,
                      label,
                    })
                  }
                  onDelete={() => deleteAttachment(att)}
                  onRename={(v) => renameAttachment(att, v)}
                  onInsertRef={() => insertRef(label)}
                />
              );
            })}
          </div>

          <div className={styles.actionRow}>
            <button
              type="button"
              className={styles.actionBtn}
              onClick={() => void captureCurrentChart()}
              title="Snapshot the focused chart"
            >
              📸 Capture chart
            </button>
            <button
              type="button"
              className={styles.actionBtn}
              onClick={onPickFile}
              title="Upload PNG from disk"
            >
              ⤴ Upload image
            </button>
            {entry && (
              <button
                type="button"
                className={`${styles.actionBtn} ${styles.actionDanger}`}
                onClick={removeEntry}
                title="Delete this note + its attachments (does not affect the trade)"
              >
                Delete note
              </button>
            )}
          </div>
        </div>
      )}

      {editing && (
        <ImageEditor
          src={editing.url}
          filename={editing.filename}
          onCancel={() => setEditing(null)}
          onSave={saveEditedAttachment}
        />
      )}

      {lightbox !== null && (
        <ImageLightbox
          items={gallery}
          startIndex={lightbox}
          onClose={() => setLightbox(null)}
          onEdit={(item) => {
            setLightbox(null);
            setEditing({
              attId: item.id,
              url: item.url,
              filename: item.filename,
              label: item.label,
            });
          }}
          onDelete={(item) => {
            const att = attachments.find((a) => a.id === item.id);
            if (att) deleteAttachment(att);
          }}
        />
      )}
    </div>
  );
}
